import { Link } from "react-router-dom";
import DOMPurify from "dompurify";
import { mediaUrl } from "@/lib/api";
import Reveal from "@/components/site/Reveal";
import ShareArticle from "@/components/site/ShareArticle";
import { formatArticleDate } from "@/pages/Diario";

/** Minimal markdown renderer: headings, paragraphs, lists, bold, links. */
export function renderMarkdown(md = "") {
  const inline = (t) =>
    t
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/\*(.+?)\*/g, "<em>$1</em>")
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-sage underline underline-offset-4">$1</a>');
  const out = [];
  let list = null;
  md.split("\n").forEach((raw) => {
    const line = raw.trim();
    if (!line) {
      if (list) {
        out.push(`<ul class="my-5 space-y-2 pl-5 list-disc">${list}</ul>`);
        list = null;
      }
      return;
    }
    if (/^- /.test(line)) {
      list = (list || "") + `<li class="leading-relaxed">${inline(line.slice(2))}</li>`;
      return;
    }
    if (list) {
      out.push(`<ul class="my-5 space-y-2 pl-5 list-disc">${list}</ul>`);
      list = null;
    }
    if (/^###\s/.test(line)) out.push(`<h3 class="mt-10 mb-3 font-serif text-2xl text-wood">${inline(line.slice(4))}</h3>`);
    else if (/^##\s/.test(line)) out.push(`<h2 class="mt-14 mb-4 font-serif text-3xl md:text-[2.2rem] text-wood">${inline(line.slice(3))}</h2>`);
    else if (/^#\s/.test(line)) out.push(`<h2 class="mt-14 mb-4 font-serif text-3xl text-wood">${inline(line.slice(2))}</h2>`);
    else out.push(`<p class="mb-6 leading-[1.85]">${inline(line)}</p>`);
  });
  if (list) out.push(`<ul class="my-5 space-y-2 pl-5 list-disc">${list}</ul>`);
  return out.join("");
}

/** Resa dell'articolo condivisa tra la pagina pubblica e l'anteprima protetta in admin. */
export function ArticleView({ article, related = [], references = [] }) {
  return (
    <article className="bg-cream pt-36 md:pt-44 pb-24" data-testid="article-page">
      <div className="container-ed max-w-3xl">
        <Link to="/diario-dal-borgo" className="eyebrow text-sage link-underline">
          Diario dal Borgo
        </Link>
        <h1 className="mt-7 font-serif text-4xl sm:text-5xl lg:text-[3.6rem] leading-[1.05] text-wood">
          {article.title}
        </h1>
        <div className="mt-6 flex flex-wrap items-center gap-x-2 text-xs tracking-wider text-foreground/50">
          <span>{article.category_name} · {formatArticleDate(article.published_at || article.created_at)}</span>
          <span className="text-foreground/30">·</span>
          <ShareArticle article={article} compact />
        </div>
        {article.excerpt && (
          <p className="mt-8 text-base md:text-xl leading-relaxed text-foreground/75">{article.excerpt}</p>
        )}
        {article.hero_image && (
          <figure className="mt-12">
            <img
              src={`${mediaUrl(article.hero_image)}?w=1200`}
              alt={article.hero_alt || article.title}
              className="w-full border border-wood/15"
              loading="lazy"
            />
            {article.image_credit && (
              <figcaption className="mt-2 text-xs text-foreground/50" data-testid="article-image-credit">
                {article.image_credit}
              </figcaption>
            )}
          </figure>
        )}
        <div
          className="mt-12 text-sm md:text-lg text-foreground/80"
          data-testid="article-body"
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(renderMarkdown(article.body_markdown), {
              ADD_ATTR: ["target", "rel"],
            }),
          }}
        />

        {references.length > 0 && (
          <details className="mt-14 border-t border-wood/15 pt-8 group" data-testid="article-science-refs">
            <summary className="flex cursor-pointer list-none items-center gap-2 text-[0.68rem] uppercase tracking-[0.2em] text-wood/60 hover:text-sage transition-colors duration-400">
              <span className="text-sage transition-transform duration-300 group-open:rotate-45">+</span>
              Fonti scientifiche ({references.length})
            </summary>
            <ol className="mt-6 space-y-4 text-sm text-foreground/65">
              {references.map((s, i) => (
                <li key={s.url || i} className="leading-relaxed">
                  <span className="text-wood">{s.title}</span>
                  {[s.journal, s.year].filter(Boolean).length > 0 && (
                    <span className="text-foreground/55">
                      {" — "}
                      {[s.journal, s.year].filter(Boolean).join(", ")}
                    </span>
                  )}
                  {(s.url || s.doi) && (
                    <>
                      {" · "}
                      <a
                        href={s.url || `https://doi.org/${s.doi}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sage underline underline-offset-4 break-all"
                      >
                        {s.doi ? `DOI ${s.doi}` : "Vai allo studio"}
                      </a>
                    </>
                  )}
                </li>
              ))}
            </ol>
          </details>
        )}

        {article.cluster === "idee-per-il-borgo" && (
          <div className="mt-16 border-t border-wood/15 pt-8" data-testid="article-idea-cta">
            <p className="text-sm text-foreground/70">
              Hai un'idea per il borgo?{" "}
              <Link to="/diario-dal-borgo/idee-per-il-borgo#modulo-idee" className="text-sage underline underline-offset-4">
                Condividila con noi
              </Link>
              .
            </p>
          </div>
        )}

        {article.cta && (
          <div className="mt-16 border-t border-wood/15 pt-10" data-testid="article-cta">
            <p className="text-sm md:text-base text-foreground/70 leading-relaxed">
              Se il tuo viaggio in Irpinia passa per Aterrana, qui trovi la nostra cucina e il borgo che
              la ospita.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/prenota" className="inline-flex px-7 py-3.5 bg-sage text-cream text-[0.68rem] uppercase tracking-[0.2em] hover:bg-wood transition-colors duration-500">
                Prenota un tavolo
              </Link>
              <Link to="/" className="inline-flex px-7 py-3.5 border border-wood/30 text-wood text-[0.68rem] uppercase tracking-[0.2em] hover:bg-wood hover:text-cream transition-colors duration-500">
                Scopri Osteria Aterrana
              </Link>
            </div>
          </div>
        )}

        <ShareArticle article={article} />

        {related.length > 0 && (
          <section className="mt-16 border-t border-wood/15 pt-10" data-testid="article-related">
            <p className="eyebrow text-sage">Da leggere anche</p>
            <ul className="mt-6 space-y-5">
              {related.map((r) => (
                <Reveal as="li" key={r.id}>
                  <Link to={`/diario-dal-borgo/${r.slug}`} className="group block">
                    <p className="eyebrow text-gold">{r.category_name}</p>
                    <h2 className="mt-2 font-serif text-2xl text-wood group-hover:text-sage transition-colors duration-400">
                      {r.title}
                    </h2>
                  </Link>
                </Reveal>
              ))}
            </ul>
          </section>
        )}
      </div>
    </article>
  );
}

export default ArticleView;
